import moment from 'moment'
import { Form, message, Button } from 'antd'
import { Formik } from 'formik'
import React, { useCallback, useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useHistory, useLocation } from 'react-router-dom'
import SplitPane from 'react-split-pane'
import api from '../../api'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import { ListError, PageHeader } from '../Common'
import { SubmitButton, UploadFormikFile } from '../Forms'
import PatientSelect from '../Forms/PatientSelect'
import PracticeSelect from '../Forms/PracticeSelect'
import PDFPreview from '../Common/PDFPreview'
import { useAsync } from '../../hooks'
import AlertForm from './AlertForm'

const propsFor = formElementPropsGenerator({
  formName: 'alerts-form',
  labels: { practice: 'Practice', patient: 'Patient' },
})

const validationSchema = Yup.object().shape({
  practice: Yup.object().required(),
  patient: Yup.object().required(),
  fileToUpload: Yup.mixed().required('You need to provide a full report'),
})

const useStyles = createUseStyles({
  app: { display: 'flex', alignItems: 'stretch', flex: '1 1 auto' },
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
  },
})

const labelCol = {
  sm: { span: 4, offset: 2 },
}

const wrapperCol = {
  sm: { span: 12 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: labelCol.sm.span + labelCol.sm.offset },
}

// eslint-disable-next-line react/prop-types
function AlertFormView({ patientId, fileToUpload, transmissionId }) {
  const history = useHistory()
  const getForm = useCallback(async () => {
    try {
      const patient = await api.patients.get(patientId)
      const form = await api.alerts.getForm(patient.currentDevice.id)
      return form
    } catch (err) {
      message.error(`There was a problem: ${err.message}`)
      return undefined
    }
  }, [patientId])
  const { error, pending, value: form } = useAsync(getForm)

  const handleCreateAlert = useCallback(
    async ({ values, critical }) => {
      const fd = new FormData()
      fd.append('patient_id', patientId)
      fd.append('critical', critical)
      fd.append('values', JSON.stringify(values))

      if (transmissionId) {
        fd.append('transmission_id', transmissionId)
      } else {
        fd.append('device_report', fileToUpload[0])
      }

      try {
        await api.alerts.create(fd)
        message.success('Alert successfully created')
        history.goBack()
      } catch (err) {
        message.error(
          'Oops! There was an error submitting your alert. Please try again',
        )
      }
    },
    [fileToUpload, history, patientId, transmissionId],
  )

  const classes = useStyles()

  if (error) {
    return (
      <ListError
        message="There was an error loading this alert"
        description={error.message}
      />
    )
  }

  if (pending) return <div>Loading...</div>

  return (
    <div className={classes.formBox}>
      <AlertForm form={form} onSubmit={handleCreateAlert} />
    </div>
  )
}

export default function New() {
  const classes = useStyles()
  const [formShow, setFormShow] = useState(false)
  const [practice, setPractice] = useState(undefined)
  const [patient, setPatient] = useState(undefined)
  const [patientId, setPatientId] = useState(undefined)
  const [transmissionId, setTransmissionId] = useState(null)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const patientIdQuery = searchParams.get('patientId')

  const getTransmission = useCallback(async () => {
    if (!transmissionId) return

    const queryTr = await api.transmissions.get(transmissionId)

    const fullUrl = queryTr.fileUrl.startsWith('http')
      ? queryTr.fileUrl
      : `${process.env.REACT_APP_API_URL}${queryTr.fileUrl}`

    setPatientId(queryTr.patient.id)
    setPatient(queryTr.patient)
    setPractice(queryTr.practice)
    setFileToUpload([fullUrl])
    setFormShow(true)
  }, [transmissionId])

  const getPatient = useCallback(async id => {
    if (!id) return
    const response = await api.patients.get(id)
    setPatient(response)
    setPatientId(response.id)
    setPractice(response.practice)
  }, [])

  useEffect(() => {
    if (patientIdQuery) {
      getPatient(patientIdQuery)
    }
  }, [getPatient, patientId, patientIdQuery])

  const [fileToUpload, setFileToUpload] = useState(null)
  const history = useHistory()
  const handleFirstForm = values => {
    setPractice(values.practice)
    setPatient(values.patient)
    setPatientId(values.patient?.key)
    setFileToUpload(values.fileToUpload)
    setFormShow(true)
  }
  const selectLabelCol = { sm: 6, offset: 0 }
  const selectWrapperCol = { sm: 12, offset: 0 }

  useEffect(() => {
    const alertInfo = JSON.parse(sessionStorage.getItem('alertInformation'))
    sessionStorage.removeItem('alertInformation')

    if (alertInfo?.transmissionId) {
      setTransmissionId(alertInfo.transmissionId)
    }
  }, [])

  useAsync(getTransmission)

  const patientLabel = patient
    ? `${patient.firstName} ${patient.lastName} - ${moment(
        patient.birthDate,
      ).format('MM-DD-YYYY')}`
    : ''

  if (formShow) {
    return (
      <div className={classes.app}>
        <SplitPane split="vertical">
          <div initialSize="65%" minSize="10%" className={classes.pdfBox}>
            <PageHeader
              title="New Alert"
              onBack={history.goBack}
              extra={
                <Button onClick={() => setFormShow(false)}>
                  Change full report
                </Button>
              }
            />
            {fileToUpload && <PDFPreview url={fileToUpload[0]} perPage={10} />}
          </div>
          <AlertFormView
            patientId={patientId}
            fileToUpload={fileToUpload}
            transmissionId={transmissionId}
          />
        </SplitPane>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="New Alert" onBack={history.goBack} />
      <Formik
        initialValues={{
          practice: practice?.id ? { key: practice.id } : undefined,
          patient: patient?.id ? { key: patient.id } : undefined,
          fileToUpload,
        }}
        onSubmit={handleFirstForm}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          isSubmitting,
          handleSubmit,
          isValid,
          submitCount,
          values,
          setFieldValue,
        }) => {
          return (
            <Form
              onSubmit={handleSubmit}
              labelCol={labelCol}
              wrapperCol={wrapperCol}
            >
              <PracticeSelect
                {...propsFor('practice')}
                labelAlign="right"
                labelInValue
                allowClear={false}
                labelCol={selectLabelCol}
                wrapperCol={selectWrapperCol}
                onChange={value => {
                  setFieldValue('practice', value)
                  setFieldValue('patient', undefined)
                }}
              />
              <PatientSelect
                {...propsFor('patient')}
                practiceId={values?.practice?.key || undefined}
                labelInValue
                labelAlign="right"
                allowClear={false}
                labelCol={selectLabelCol}
                wrapperCol={selectWrapperCol}
                disabled={patient ? false : !values.practice}
                onChange={value => {
                  setFieldValue('patient', value)
                }}
                options={
                  patientIdQuery === values?.patient?.key
                    ? [{ id: patientIdQuery, name: patientLabel }]
                    : undefined
                }
              />
              <UploadFormikFile
                name="fileToUpload"
                label="Full Report"
                supportFiles={['application/pdf']}
              />
              <SubmitButton
                title="Next"
                isValid={isValid}
                submitCount={submitCount}
                isSubmitting={isSubmitting}
                wrapperCol={actionsWrapperCol}
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
